import { MouseEvent, useLayoutEffect, useState } from 'react';

import createToolbarPlugin from '@draft-js-plugins/static-toolbar';
import { RichUtils } from 'draft-js';
import styled from 'styled-components';

import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { getSelectedBlock } from '@utils/draftjs';
import { transitionStandard } from '@utils/transitions';

import {
  TextEditorBold,
  TextEditorBulletList,
  TextEditorHeading,
  TextEditorItalic,
  TextEditorNumberedList,
  TextEditorUnderline,
} from './icons';
import { AnchorButton, AnchorButtonProps } from './plugin-anchor';
import { useTextEditorContext } from './text-editor';

type TextEditorPluginToolbarProps = {
  anchorButtonProps: AnchorButtonProps;
  disableHeading?: boolean;
};

export function TextEditorPluginToolbar({
  disableHeading,
  anchorButtonProps,
}: TextEditorPluginToolbarProps) {
  let { registerPlugin, readOnly } = useTextEditorContext();
  const [toolbarPlugin] = useState(() => {
    return createToolbarPlugin({
      theme: {
        toolbarStyles: {
          toolbar: 'rdw-text-editor-toolbar',
        },
        buttonStyles: {},
      },
    });
  });

  useLayoutEffect(() => {
    registerPlugin('toolbar', toolbarPlugin);
  }, [registerPlugin, toolbarPlugin]);

  if (readOnly) return null;

  return (
    <Stack orientation="horizontal" align="center" space={1} style={{ height: theme.base.height }}>
      {!disableHeading && (
        <BlockStyleButton icon={<TextEditorHeading />} blockType="header-three" />
      )}
      <InlineStyleButton icon={<TextEditorBold />} blockType="BOLD" />
      <InlineStyleButton icon={<TextEditorItalic />} blockType="ITALIC" />
      <InlineStyleButton icon={<TextEditorUnderline />} blockType="UNDERLINE" />
      <Separator orientation="horizontal" style={{ height: pxToRem(17) }} />
      <BlockStyleButton icon={<TextEditorNumberedList />} blockType="ordered-list-item" />
      <BlockStyleButton icon={<TextEditorBulletList />} blockType="unordered-list-item" />
      <Separator orientation="horizontal" style={{ height: pxToRem(17) }} />
      <AnchorButton {...anchorButtonProps} />
    </Stack>
  );
}

function InlineStyleButton({ blockType, icon }: { blockType: string; icon: JSX.Element }) {
  const { editorState, setEditorState } = useTextEditorContext();
  const toggle = (e: MouseEvent) => {
    e.preventDefault();
    const newState = RichUtils.toggleInlineStyle(editorState, blockType);
    if (newState) {
      setEditorState(newState);
    }
  };

  const active = editorState.getCurrentInlineStyle().has(blockType);

  return (
    <ToolbarButton data-active={active} onClick={toggle} onMouseDown={(e) => e.preventDefault()}>
      {icon}
    </ToolbarButton>
  );
}

function BlockStyleButton({ blockType, icon }: { blockType: string; icon: JSX.Element }) {
  const { editorState, setEditorState } = useTextEditorContext();

  const toggle = (e: MouseEvent) => {
    e.preventDefault();
    const newState = RichUtils.toggleBlockType(editorState, blockType);
    if (newState) {
      setEditorState(newState);
    }
  };

  const active = getSelectedBlock(editorState)?.getType() === blockType;

  return (
    <ToolbarButton data-active={active} onClick={toggle} onMouseDown={(e) => e.preventDefault()}>
      {icon}
    </ToolbarButton>
  );
}

const ToolbarButton = styled.button({
  transition: transitionStandard('all'),
  borderRadius: '0.25rem',
  padding: 0,
  border: 'none',
  background: 'transparent',
  color: theme.color.buttonIconPositive,
  height: theme.base.height,
  width: theme.base.height,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  outline: 'none',

  '&:focus-visible': {
    boxShadow: theme.shadow.focus,
    backgroundColor: theme.color.uiTrans10,
    color: theme.color.buttonIconPositiveHover,
    '--shadow-focus-color': theme.color.purple50,
    '--shadow-focus-gap': 0,
    '--shadow-focus-width': '0.1rem',
  },

  '&:active': {
    transform: 'scale(0.97)',
  },

  '&[data-active="true"]': {
    color: theme.color.elementBlue100,
  },

  '&:hover': {
    cursor: 'pointer',
    backgroundColor: theme.color.uiTrans10,

    '&[data-active="false"]': {
      color: theme.color.buttonIconPositiveHover,
    },
  },
});
